import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import {
  iosNativeContent,
  iosNativeMarketingHero,
  iosNativePageRoot,
} from '../styles/iosNativePageCss';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { Badge } from '../components/ui/Badge';
import { Check, Video, Shield, Users, Zap, FileText, MessageCircle, ArrowRight, Star } from 'lucide-react';

const PageContainer = styled.div`
  min-height: calc(100vh - 72px);
  background: linear-gradient(180deg, ${theme.colors.background.secondary} 0%, ${theme.colors.background.primary} 100%);
  padding: 2rem 1rem 4rem;

  ${iosNativePageRoot}
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;

  ${iosNativeContent}
`;

const HeroSection = styled.section`
  text-align: center;
  margin-bottom: 4rem;
  animation: slideUp 0.6s ease-out;

  ${iosNativeMarketingHero}
`;

const HeroTitle = styled.h1`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['5xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 1rem;
  line-height: ${theme.typography.lineHeight.tight};

  @media (max-width: ${theme.breakpoints.md}) {
    font-size: ${theme.typography.fontSize['4xl']};
  }
`;

const HeroSubtitle = styled.p`
  font-size: ${theme.typography.fontSize.xl};
  color: ${theme.colors.text.secondary};
  max-width: 700px;
  margin: 0 auto 2rem;
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const HeroCTA = styled.div`
  display: flex;
  gap: 1rem;
  justify-content: center;
  flex-wrap: wrap;
  margin-top: 2rem;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 2rem;
  margin-bottom: 4rem;
`;

const BenefitCard = styled(Card)`
  padding: 2rem;
  text-align: center;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-4px);
    box-shadow: ${theme.shadows.lg};
  }
`;

const BenefitIcon = styled.div<{ $color: string }>`
  width: 64px;
  height: 64px;
  margin: 0 auto 1.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${props => props.$color}20;
  border-radius: ${theme.borderRadius.xl};
  color: ${props => props.$color};

  svg {
    width: 32px;
    height: 32px;
  }
`;

const BenefitTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.xl};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  margin-bottom: 0.75rem;
`;

const BenefitDescription = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
`;

const ComparisonSection = styled.section`
  margin-bottom: 4rem;
`;

const SectionTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  color: ${theme.colors.text.primary};
  text-align: center;
  margin-bottom: 2rem;
`;

const ComparisonTable = styled(Card)`
  padding: 0;
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
`;

const TableHeader = styled.thead`
  background: ${theme.colors.background.secondary};
`;

const TableRow = styled.tr`
  border-bottom: 1px solid ${theme.colors.border.light};

  &:last-child {
    border-bottom: none;
  }
`;

const TableHeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  text-transform: uppercase;
  letter-spacing: 0.05em;

  &:first-child {
    position: sticky;
    left: 0;
    background: ${theme.colors.background.secondary};
    z-index: 1;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  text-align: center;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};

  &:first-child {
    text-align: left;
    font-weight: ${theme.typography.fontWeight.medium};
    color: ${theme.colors.text.primary};
    position: sticky;
    left: 0;
    background: ${theme.colors.background.primary};
    z-index: 1;
  }
`;

const CheckIcon = styled(Check)`
  width: 20px;
  height: 20px;
  color: ${theme.colors.mint[600]};
  margin: 0 auto;
`;

const DashIcon = styled.span`
  color: ${theme.colors.text.tertiary};
  font-size: ${theme.typography.fontSize.lg};
`;

const TestimonialsSection = styled.section`
  margin-bottom: 4rem;
`;

const TestimonialsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin-top: 2rem;
`;

const TestimonialCard = styled(Card)`
  padding: 1.5rem;
`;

const TestimonialText = styled.p`
  font-size: ${theme.typography.fontSize.base};
  color: ${theme.colors.text.secondary};
  line-height: ${theme.typography.lineHeight.relaxed};
  margin-bottom: 1rem;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const AuthorAvatar = styled.div`
  width: 40px;
  height: 40px;
  border-radius: ${theme.borderRadius.full};
  background: ${theme.colors.primary[400]};
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-weight: ${theme.typography.fontWeight.bold};
`;

const AuthorInfo = styled.div`
  flex: 1;
`;

const AuthorName = styled.div`
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.sm};
`;

const AuthorRole = styled.div`
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.tertiary};
`;

const Stars = styled.div`
  display: flex;
  gap: 0.25rem;
  margin-bottom: 0.5rem;
`;

const CTAFooter = styled.div`
  text-align: center;
  padding: 3rem 0;
  background: linear-gradient(135deg, ${theme.colors.primary[500]} 0%, ${theme.colors.mint[600]} 100%);
  border-radius: ${theme.borderRadius['2xl']};
  color: white;
  margin-top: 4rem;
`;

const CTAFooterTitle = styled.h2`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize['3xl']};
  font-weight: ${theme.typography.fontWeight.bold};
  margin-bottom: 1rem;
`;

const CTAFooterText = styled.p`
  font-size: ${theme.typography.fontSize.lg};
  margin-bottom: 2rem;
  opacity: 0.95;
`;

const ElitePage: React.FC = () => {
  const navigate = useNavigate();
  const [isYearly, setIsYearly] = useState(false);

  const handleStartTrial = () => {
    // TODO: Wire to Stripe checkout for Elite trial
    console.log('Starting Elite 7-day free trial...');
    // navigate('/checkout?plan=elite&trial=true');
  };

  const handleComparePlans = () => {
    navigate('/pricing');
  };

  const comparisonData = [
    {
      feature: 'Monthly scans',
      free: '3/week',
      plus: 'Unlimited',
      pro: 'Unlimited',
      elite: 'Unlimited',
    },
    {
      feature: 'Acne analysis',
      free: 'Basic',
      plus: 'Advanced',
      pro: 'Most Advanced',
      elite: 'Most Advanced',
    },
    {
      feature: 'DIY mask library',
      free: '3 masks',
      plus: '10 masks',
      pro: 'Unlimited',
      elite: 'Unlimited',
    },
    {
      feature: 'Custom diet plan',
      free: '—',
      plus: '✓',
      pro: '✓ Advanced',
      elite: '✓ Advanced',
    },
    {
      feature: 'Restaurant food scanner',
      free: '—',
      plus: '—',
      pro: '✓',
      elite: '✓',
    },
    {
      feature: 'Progress tracking',
      free: '—',
      plus: '✓',
      pro: '✓',
      elite: '✓',
    },
    {
      feature: 'Habit tracking',
      free: '—',
      plus: '—',
      pro: '✓',
      elite: '✓',
    },
    {
      feature: 'Weekly insights',
      free: '—',
      plus: '—',
      pro: '✓',
      elite: '✓',
    },
    {
      feature: 'Export reports',
      free: '—',
      plus: '—',
      pro: '—',
      elite: '✓',
    },
    {
      feature: 'Tele-dermatology consult',
      free: '—',
      plus: '—',
      pro: '—',
      elite: '1x/month',
    },
    {
      feature: 'Prescription-aware guidance',
      free: '—',
      plus: '—',
      pro: '—',
      elite: '✓',
    },
    {
      feature: 'Priority AI processing',
      free: '—',
      plus: '—',
      pro: '—',
      elite: '✓',
    },
    {
      feature: 'Family plan (3 accounts)',
      free: '—',
      plus: '—',
      pro: '—',
      elite: '✓',
    },
    {
      feature: 'VIP support (24hr response)',
      free: '—',
      plus: '—',
      pro: '—',
      elite: '✓',
    },
  ];

  const testimonials = [
    {
      text: "Elite's tele-derm consult helped me understand my prescription routine better. The ingredient conflict warnings are a game-changer!",
      author: 'Sarah M.',
      role: 'Severe acne, prescription user',
      rating: 5,
    },
    {
      text: "As a parent managing my teen's skincare, the family plan is perfect. We both get personalized guidance without doubling the cost.",
      author: 'Michael R.',
      role: 'Parent of teen with acne',
      rating: 5,
    },
    {
      text: "The priority processing means I get my scan results in seconds. Plus, the monthly reports help me track progress with my dermatologist.",
      author: 'Jessica L.',
      role: 'Skincare enthusiast',
      rating: 5,
    },
  ];

  return (
    <PageContainer>
      <Container>
        <HeroSection>
          <HeroTitle>
            GlowLens Elite: dermatologist-level support, without the waiting room. ✨
          </HeroTitle>
          <HeroSubtitle>
            For those with stubborn acne, complex skin concerns, prescription routines, or families who want expert-level guidance. Get real dermatologist consultations, prescription-aware product recommendations, and priority AI processing—all in one premium plan.
          </HeroSubtitle>
          <HeroCTA>
            <Button
              size="lg"
              variant="primary"
              onClick={handleStartTrial}
            >
              Start Elite 7-Day Free Trial
            </Button>
            <Button
              size="lg"
              variant="outline"
              onClick={handleComparePlans}
            >
              Compare all plans →
            </Button>
          </HeroCTA>
        </HeroSection>

        <BenefitsGrid>
          <BenefitCard>
            <BenefitIcon $color={theme.colors.primary[600]}>
              <Video />
            </BenefitIcon>
            <BenefitTitle>Real Experts, Not Just AI</BenefitTitle>
            <BenefitDescription>
              Monthly video consultations with licensed dermatologists. Get personalized advice, routine reviews, and expert guidance tailored to your unique skin journey.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon $color={theme.colors.mint[600]}>
              <Shield />
            </BenefitIcon>
            <BenefitTitle>Prescription-Aware Guidance</BenefitTitle>
            <BenefitDescription>
              Enter your prescription medications and allergies. GlowLens flags ingredient conflicts in products and diet recommendations, keeping you safe and informed.
            </BenefitDescription>
          </BenefitCard>

          <BenefitCard>
            <BenefitIcon $color={theme.colors.accent.lavender}>
              <Users />
            </BenefitIcon>
            <BenefitTitle>Family-Friendly Plan</BenefitTitle>
            <BenefitDescription>
              Link up to 3 sub-accounts under one Elite subscription. Perfect for parents managing their teen's skincare or families who want personalized guidance for everyone.
            </BenefitDescription>
          </BenefitCard>
        </BenefitsGrid>

        <ComparisonSection>
          <SectionTitle>What's in Elite? 🎯</SectionTitle>
          <ComparisonTable>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Feature</TableHeaderCell>
                  <TableHeaderCell>Free</TableHeaderCell>
                  <TableHeaderCell>Plus</TableHeaderCell>
                  <TableHeaderCell>Pro</TableHeaderCell>
                  <TableHeaderCell>Elite</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <tbody>
                {comparisonData.map((row, index) => (
                  <TableRow key={index}>
                    <TableCell>{row.feature}</TableCell>
                    <TableCell>{row.free === '—' ? <DashIcon>—</DashIcon> : row.free}</TableCell>
                    <TableCell>{row.plus === '—' ? <DashIcon>—</DashIcon> : row.plus === '✓' ? <CheckIcon /> : row.plus}</TableCell>
                    <TableCell>{row.pro === '—' ? <DashIcon>—</DashIcon> : row.pro === '✓' ? <CheckIcon /> : row.pro}</TableCell>
                    <TableCell>{row.elite === '—' ? <DashIcon>—</DashIcon> : row.elite === '✓' ? <CheckIcon /> : row.elite}</TableCell>
                  </TableRow>
                ))}
              </tbody>
            </Table>
          </ComparisonTable>
        </ComparisonSection>

        <TestimonialsSection>
          <SectionTitle>What Elite Members Say 💬</SectionTitle>
          <TestimonialsGrid>
            {testimonials.map((testimonial, index) => (
              <TestimonialCard key={index}>
                <Stars>
                  {Array.from({ length: testimonial.rating }).map((_, i) => (
                    <Star key={i} size={16} fill={theme.colors.status.warning} color={theme.colors.status.warning} />
                  ))}
                </Stars>
                <TestimonialText>"{testimonial.text}"</TestimonialText>
                <TestimonialAuthor>
                  <AuthorAvatar>
                    {testimonial.author.charAt(0)}
                  </AuthorAvatar>
                  <AuthorInfo>
                    <AuthorName>{testimonial.author}</AuthorName>
                    <AuthorRole>{testimonial.role}</AuthorRole>
                  </AuthorInfo>
                </TestimonialAuthor>
              </TestimonialCard>
            ))}
          </TestimonialsGrid>
        </TestimonialsSection>

        <CTAFooter>
          <CTAFooterTitle>Ready to unlock Elite? 🚀</CTAFooterTitle>
          <CTAFooterText>
            Start your 7-day free trial. No credit card required. Cancel anytime.
          </CTAFooterText>
          <Button
            size="lg"
            variant="secondary"
            onClick={handleStartTrial}
            style={{ background: 'white', color: theme.colors.primary[600] }}
          >
            Start Elite 7-Day Free Trial
            <ArrowRight size={20} style={{ marginLeft: '0.5rem' }} />
          </Button>
        </CTAFooter>
      </Container>
    </PageContainer>
  );
};

export default ElitePage;




